import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:    '#0A1628', gold:    '#C9A84C', black:   '#0A0A0A',
  white:   '#FFFFFF', surface: '#F5F6F8', border:  '#D0D5DD',
  borderLt:'#E8EBF0', text:    '#0A0A0A', textSec: '#3D4552',
  muted:   '#6B7280', mutedLt: '#9CA3AF', success: '#0A7C42',
  danger:  '#C0222A', amber:   '#92640A',
};

interface Meeting {
  id: string; connection_id: string; title: string; scheduled_at: string;
  duration_minutes: number; status: string; notes: string;
  daily_room_url: string; daily_room_name: string; proposed_by: string;
  recipient_firm_id: string; meeting_type: string; location: string; connection?: any;
}

const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const dayNames   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px',
  padding: '8px 10px', outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
  textTransform: 'uppercase', display: 'block', marginBottom: '5px', fontFamily: 'system-ui',
};
const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

export default function Calendar() {
  const { user } = useAuth();
  const { firm }  = useProfile();
  const [meetings, setMeetings]         = useState<Meeting[]>([]);
  const [loading, setLoading]           = useState(true);
  const [currentDate, setCurrentDate]   = useState(new Date());
  const [showSchedule, setShowSchedule] = useState(false);
  const [connections, setConnections]   = useState<any[]>([]);
  const [responding, setResponding]     = useState<string | null>(null);
  const [scheduling, setScheduling]     = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleForm, setScheduleForm] = useState({
    connection_id: '', title: '', date: '', time: '10:00',
    duration_minutes: 60, notes: '', meeting_type: 'video', location: '',
  });

  useEffect(() => { if (firm) { fetchMeetings(); fetchConnections(); } }, [firm]);

  const fetchMeetings = async () => {
    if (!firm) return;
    const { data: connData } = await supabase.from('connections').select('id')
      .eq('status', 'approved').or(`allocator_firm_id.eq.${firm.id},manager_firm_id.eq.${firm.id}`);
    if (!connData?.length) { setLoading(false); return; }
    const { data } = await supabase.from('meetings')
      .select(`*, connection:connections(
        allocator_firm:firms!connections_allocator_firm_id_fkey(id, name),
        manager_firm:firms!connections_manager_firm_id_fkey(id, name),
        fund:funds(id, name))`)
      .in('connection_id', connData.map((c: any) => c.id))
      .order('scheduled_at', { ascending: true });
    if (data) setMeetings(data);
    setLoading(false);
  };

  const fetchConnections = async () => {
    if (!firm) return;
    const { data } = await supabase.from('connections')
      .select(`id,
        allocator_firm:firms!connections_allocator_firm_id_fkey(id, name),
        manager_firm:firms!connections_manager_firm_id_fkey(id, name),
        fund:funds(id, name)`)
      .eq('status', 'approved').or(`allocator_firm_id.eq.${firm.id},manager_firm_id.eq.${firm.id}`);
    if (data) {
      const norm = data.map((c: any) => ({
        ...c,
        allocator_firm: Array.isArray(c.allocator_firm) ? c.allocator_firm[0] : c.allocator_firm,
        manager_firm:   Array.isArray(c.manager_firm)   ? c.manager_firm[0]   : c.manager_firm,
        fund:           Array.isArray(c.fund)           ? c.fund[0]           : c.fund,
      }));
      setConnections(norm);
      if (norm.length > 0) setScheduleForm(f => ({ ...f, connection_id: norm[0].id }));
    }
  };

  const getOtherFirm = (conn: any) => {
    const af = Array.isArray(conn.allocator_firm) ? conn.allocator_firm[0] : conn.allocator_firm;
    const mf = Array.isArray(conn.manager_firm)   ? conn.manager_firm[0]   : conn.manager_firm;
    return af?.id === firm?.id ? mf?.name : af?.name;
  };
  const getOtherFirmFromMeeting = (m: Meeting) => {
    const conn = m.connection as any; if (!conn || !firm) return '';
    const af = Array.isArray(conn.allocator_firm) ? conn.allocator_firm[0] : conn.allocator_firm;
    const mf = Array.isArray(conn.manager_firm)   ? conn.manager_firm[0]   : conn.manager_firm;
    return af?.id === firm.id ? mf?.name : af?.name;
  };

  const scheduleMeeting = async () => {
    if (!user || !scheduleForm.connection_id || !scheduleForm.title || !scheduleForm.date) { setScheduleError('Please fill in all required fields.'); return; }
    if (scheduleForm.meeting_type === 'in_person' && !scheduleForm.location.trim()) { setScheduleError('Please enter a location.'); return; }
    setScheduling(true); setScheduleError('');
    try {
      const scheduledAt = new Date(`${scheduleForm.date}T${scheduleForm.time}`).toISOString();
      const conn = connections.find(c => c.id === scheduleForm.connection_id);
      const recipientFirmId = conn?.allocator_firm?.id === firm?.id ? conn?.manager_firm?.id : conn?.allocator_firm?.id;
      const { error } = await supabase.from('meetings').insert({
        connection_id: scheduleForm.connection_id, proposed_by: user.id,
        recipient_firm_id: recipientFirmId, title: scheduleForm.title,
        scheduled_at: scheduledAt, duration_minutes: scheduleForm.duration_minutes,
        notes: scheduleForm.notes, meeting_type: scheduleForm.meeting_type,
        location: scheduleForm.meeting_type === 'in_person' ? scheduleForm.location : null, status: 'pending',
      });
      if (error) throw new Error(error.message);
      setShowSchedule(false);
      setScheduleForm(f => ({ ...f, title: '', date: '', time: '10:00', notes: '', location: '' }));
      fetchMeetings();
    } catch (err: any) { setScheduleError(err.message); }
    setScheduling(false);
  };

  const respondToMeeting = async (meetingId: string, accept: boolean) => {
    setResponding(meetingId);
    try {
      if (accept) {
        const roomName = `altveno-${Date.now()}`;
        const fnRes = await supabase.functions.invoke('create-daily-room', { body: { meeting_name: roomName, expires_in_hours: 72 } });
        if (fnRes.error) throw new Error(fnRes.error.message);
        await supabase.from('meetings').update({ status: 'confirmed', daily_room_url: fnRes.data.url, daily_room_name: fnRes.data.name }).eq('id', meetingId);
      } else {
        await supabase.from('meetings').update({ status: 'cancelled' }).eq('id', meetingId);
      }
      await supabase.functions.invoke('handle-meeting-notification', { body: { meeting_id: meetingId, new_status: accept ? 'confirmed' : 'cancelled' } });
      fetchMeetings();
    } catch (err) { console.error(err); }
    setResponding(null);
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const getMeetingsForDay = (day: number) =>
    meetings.filter(m => {
      if (!m.scheduled_at) return false;
      const d = new Date(m.scheduled_at);
      return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day
        && (m.status === 'confirmed' || m.status === 'scheduled');
    });

  const pendingForMe  = meetings.filter(m => m.status === 'pending' && m.recipient_firm_id === firm?.id);
  const pendingByMe   = meetings.filter(m => m.status === 'pending' && m.proposed_by === user?.id);
  const upcomingMeetings = meetings.filter(m => m.status === 'confirmed' && m.scheduled_at && new Date(m.scheduled_at) >= today);
  const pastMeetings     = meetings.filter(m => m.status === 'confirmed' && m.scheduled_at && new Date(m.scheduled_at) < today);

  if (loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading calendar...</div>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '22px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>Calendar</h1>
          <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Schedule and manage meetings with your connections</p>
        </div>
        <button onClick={() => setShowSchedule(true)} style={{
          background: C.navy, border: 'none', borderRadius: '3px',
          color: C.white, fontSize: '12px', fontWeight: 600,
          padding: '8px 16px', cursor: 'pointer', fontFamily: 'system-ui', letterSpacing: '0.2px',
        }}>+ Schedule Meeting</button>
      </div>

      {/* ── Pending invites ── */}
      {pendingForMe.length > 0 && (
        <div style={{ ...card, marginBottom: '16px', borderLeft: `3px solid ${C.amber}` }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: C.amber, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
              Meeting Invites, {pendingForMe.length} Pending
            </div>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {pendingForMe.map(m => {
              const d = new Date(m.scheduled_at);
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.text, marginBottom: '3px' }}>{m.title}</div>
                    <div style={{ fontSize: '11px', color: C.muted, marginBottom: '2px' }}>From: {getOtherFirmFromMeeting(m)}</div>
                    <div style={{ fontSize: '11px', color: C.muted, marginBottom: '2px' }}>
                      {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · {m.duration_minutes} min
                    </div>
                    <div style={{ fontSize: '11px', color: C.muted }}>
                      {m.meeting_type === 'in_person' ? `In person · ${m.location || 'TBC'}` : 'Video call'}
                    </div>
                    {m.notes && <div style={{ fontSize: '11px', color: C.mutedLt, marginTop: '3px', fontStyle: 'italic' }}>{m.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '16px' }}>
                    <button onClick={() => respondToMeeting(m.id, false)} disabled={responding === m.id} style={{
                      background: C.white, border: `1px solid ${C.danger}`,
                      borderRadius: '3px', color: C.danger,
                      fontSize: '11.5px', fontWeight: 600, padding: '6px 12px',
                      cursor: 'pointer', fontFamily: 'system-ui',
                    }}>Decline</button>
                    <button onClick={() => respondToMeeting(m.id, true)} disabled={responding === m.id} style={{
                      background: C.navy, border: 'none',
                      borderRadius: '3px', color: C.white,
                      fontSize: '11.5px', fontWeight: 600, padding: '6px 12px',
                      cursor: 'pointer', fontFamily: 'system-ui',
                      opacity: responding === m.id ? 0.7 : 1,
                    }}>{responding === m.id ? '...' : 'Accept'}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '16px' }}>

        {/* ── Calendar grid ── */}
        <div style={card}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} style={{
              background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
              color: C.textSec, padding: '4px 10px', cursor: 'pointer', fontSize: '14px',
            }}>‹</button>
            <span style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy, fontWeight: 400 }}>
              {monthNames[month]} {year}
            </span>
            <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} style={{
              background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
              color: C.textSec, padding: '4px 10px', cursor: 'pointer', fontSize: '14px',
            }}>›</button>
          </div>

          <div style={{ padding: '12px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '6px' }}>
              {dayNames.map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: 700, color: C.muted, padding: '4px', letterSpacing: '0.5px' }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const dayMeetings = getMeetingsForDay(day);
                const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
                return (
                  <div key={day}
                    onClick={() => {
                      if (dayMeetings.length === 0) {
                        setScheduleForm(f => ({ ...f, date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` }));
                        setShowSchedule(true);
                      }
                    }}
                    style={{
                      minHeight: '56px', padding: '5px',
                      borderRadius: '3px', cursor: 'pointer',
                      background: isToday ? '#EBF0FA' : 'transparent',
                      border: isToday ? `1px solid ${C.navy}` : `1px solid transparent`,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => { if (!isToday) (e.currentTarget as HTMLDivElement).style.background = C.surface; }}
                    onMouseLeave={e => { if (!isToday) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: isToday ? 700 : 400, color: isToday ? C.navy : C.text, marginBottom: '3px' }}>{day}</div>
                    {dayMeetings.slice(0, 2).map(m => (
                      <div key={m.id} style={{
                        fontSize: '9px', background: C.navy, color: C.gold,
                        borderRadius: '2px', padding: '1px 4px', marginBottom: '2px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontWeight: 600,
                      }}>
                        {new Date(m.scheduled_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} {m.title}
                      </div>
                    ))}
                    {dayMeetings.length > 2 && (
                      <div style={{ fontSize: '9px', color: C.mutedLt, fontWeight: 600 }}>+{dayMeetings.length - 2}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Pending sent */}
          {pendingByMe.length > 0 && (
            <div style={card}>
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: C.amber, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                  Awaiting Response · {pendingByMe.length}
                </div>
              </div>
              <div style={{ padding: '8px 14px' }}>
                {pendingByMe.map((m, i) => {
                  const d = new Date(m.scheduled_at);
                  return (
                    <div key={m.id} style={{ padding: '8px 0', borderBottom: i < pendingByMe.length - 1 ? `1px solid ${C.borderLt}` : 'none' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: C.text }}>{m.title}</div>
                      <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
                        {getOtherFirmFromMeeting(m)} · {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upcoming */}
          <div style={card}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                Upcoming · {upcomingMeetings.length}
              </div>
            </div>
            <div style={{ padding: '8px 14px' }}>
              {upcomingMeetings.length === 0 ? (
                <div style={{ fontSize: '12px', color: C.muted, padding: '8px 0' }}>No confirmed meetings</div>
              ) : upcomingMeetings.slice(0, 5).map((m, i) => {
                const d = new Date(m.scheduled_at);
                return (
                  <div key={m.id} style={{ padding: '10px 0', borderBottom: i < Math.min(upcomingMeetings.length, 5) - 1 ? `1px solid ${C.borderLt}` : 'none' }}>
                    <div style={{ fontSize: '12.5px', fontWeight: 600, color: C.text, marginBottom: '3px' }}>{m.title}</div>
                    <div style={{ fontSize: '11px', color: C.muted, marginBottom: '2px' }}>
                      {getOtherFirmFromMeeting(m)}
                    </div>
                    <div style={{ fontSize: '11px', color: C.muted, marginBottom: '2px', fontFamily: '"Courier New", monospace' }}>
                      {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · {m.duration_minutes}min
                    </div>
                    <div style={{ fontSize: '10px', color: C.mutedLt, marginBottom: m.daily_room_url ? '8px' : '0' }}>
                      {m.meeting_type === 'in_person' ? `In person · ${m.location || 'TBC'}` : 'Video call'}
                    </div>
                    {m.daily_room_url && (
                      <button onClick={() => window.open(m.daily_room_url, '_blank')} style={{
                        width: '100%', background: C.navy, border: 'none',
                        borderRadius: '3px', color: C.white,
                        fontSize: '11.5px', fontWeight: 600, padding: '6px',
                        cursor: 'pointer', fontFamily: 'system-ui',
                      }}>Join Video Call →</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Past */}
          {pastMeetings.length > 0 && (
            <div style={card}>
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                  Past · {pastMeetings.length}
                </div>
              </div>
              <div style={{ padding: '8px 14px' }}>
                {pastMeetings.slice(-3).reverse().map((m, i) => {
                  const d = new Date(m.scheduled_at);
                  return (
                    <div key={m.id} style={{ padding: '8px 0', borderBottom: i < 2 ? `1px solid ${C.borderLt}` : 'none' }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: C.mutedLt }}>{m.title}</div>
                      <div style={{ fontSize: '11px', color: C.mutedLt, marginTop: '2px' }}>
                        {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Schedule modal ── */}
      {showSchedule && (
        <div onClick={() => setShowSchedule(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white, border: `1px solid ${C.border}`,
            borderRadius: '4px', padding: '24px', width: '440px',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '12px', marginBottom: '18px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '4px' }}>New Meeting</div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: C.navy, fontFamily: 'Georgia, serif' }}>Schedule a Meeting</div>
            </div>

            {scheduleError && (
              <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: '3px', padding: '8px 12px', marginBottom: '14px', fontSize: '12px', color: C.danger }}>
                {scheduleError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Send Invite To</label>
                <select value={scheduleForm.connection_id} onChange={e => setScheduleForm(f => ({ ...f, connection_id: e.target.value }))} style={{ ...inputStyle }}>
                  {connections.map(c => (
                    <option key={c.id} value={c.id}>{getOtherFirm(c)}, {c.fund?.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Meeting Title</label>
                <input value={scheduleForm.title} onChange={e => setScheduleForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Introductory call, IDD review" style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input type="date" value={scheduleForm.date} onChange={e => setScheduleForm(f => ({ ...f, date: e.target.value }))} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
                </div>
                <div>
                  <label style={labelStyle}>Time</label>
                  <input type="time" value={scheduleForm.time} onChange={e => setScheduleForm(f => ({ ...f, time: e.target.value }))} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Duration</label>
                <select value={scheduleForm.duration_minutes} onChange={e => setScheduleForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) }))} style={{ ...inputStyle }}>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Meeting Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  {[{ value: 'video', label: 'Video Call' }, { value: 'in_person', label: 'In Person' }].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setScheduleForm(f => ({ ...f, meeting_type: opt.value }))} style={{
                      padding: '8px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer',
                      fontFamily: 'system-ui', fontWeight: 500,
                      border: `1px solid ${scheduleForm.meeting_type === opt.value ? C.navy : C.border}`,
                      background: scheduleForm.meeting_type === opt.value ? C.navy : C.white,
                      color: scheduleForm.meeting_type === opt.value ? C.white : C.textSec,
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>
              {scheduleForm.meeting_type === 'in_person' && (
                <div>
                  <label style={labelStyle}>Location</label>
                  <input value={scheduleForm.location} onChange={e => setScheduleForm(f => ({ ...f, location: e.target.value }))}
                    placeholder="e.g. 1 Canada Square, London" style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
                </div>
              )}
              <div>
                <label style={labelStyle}>Notes (Optional)</label>
                <textarea value={scheduleForm.notes} onChange={e => setScheduleForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Agenda, topics to cover..." rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '18px' }}>
              <button onClick={() => setShowSchedule(false)} style={{
                background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
                color: C.muted, fontSize: '12.5px', padding: '9px', cursor: 'pointer', fontFamily: 'system-ui',
              }}>Cancel</button>
              <button onClick={scheduleMeeting} disabled={scheduling} style={{
                background: C.navy, border: 'none', borderRadius: '3px',
                color: C.white, fontSize: '12.5px', fontWeight: 600,
                padding: '9px', cursor: scheduling ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui', opacity: scheduling ? 0.7 : 1,
              }}>{scheduling ? 'Sending...' : 'Send Invite'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}